/**
 * Utility functions for CallsTable formatting
 */

import { parseISO, isValid } from "date-fns";

/**
 * Returns a UTC-based date key (YYYY-MM-DD) for calendar day comparison.
 */
const toUTCDayKey = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Determines whether to display the updatedAt date below createdAt in the "Dodano" column.
 * Returns true only when updatedAt falls on a different calendar day (UTC) than createdAt.
 */
export const shouldShowUpdatedAt = (
  createdAt: string | null | undefined,
  updatedAt: string | null | undefined
): boolean => {
  if (!createdAt || !updatedAt) return false;
  try {
    const created = parseISO(createdAt);
    const updated = parseISO(updatedAt);
    if (!isValid(created) || !isValid(updated)) return false;
    return toUTCDayKey(created) !== toUTCDayKey(updated);
  } catch {
    return false;
  }
};

interface LabelEntity {
  name: string;
  code?: string | null;
}

/**
 * Formats project label: "Name (CODE)" or just "Name" if no code
 * Returns null if project is null or name is empty
 */
export const formatProjectLabel = (
  project: LabelEntity | null | undefined
): string | null => {
  if (!project || !project.name || project.name.trim() === "") return null;
  if (project.code && project.code.trim() !== "") {
    return `${project.name} (${project.code})`;
  }
  return project.name;
};

/**
 * Formats competition label: "Name (CODE)" or just "Name" if no code
 * Returns null if competition is null or name is empty
 */
export const formatCompetitionLabel = (
  competition: LabelEntity | null | undefined
): string | null => {
  if (!competition || !competition.name || competition.name.trim() === "") return null;
  if (competition.code && competition.code.trim() !== "") {
    return `${competition.name} (${competition.code})`;
  }
  return competition.name;
};
